import { formatDefaultLocale, formatLocale } from 'd3-format';
import { scaleZoned } from 'd3-luxon';
import { timeFormatDefaultLocale } from 'd3-time-format';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import {
  VictoryAxis, VictoryBar, VictoryChart, VictoryLabel,
  VictoryStack, VictoryTooltip, VictoryVoronoiContainer,
} from 'victory';

import ChartTooltip from 'src/enosikit/components/Chart/components/ChartTooltip';
import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PORTFOLIO, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE, X_AXIS_DOMAIN_DYNAMIC, X_AXIS_DOMAIN_FIXED,
} from 'src/util/constants';
import { getChartLocale, getCurrencyChartLocale } from 'src/util/i18n/constants';
import { getCurrencySymbol, useIntlCurrency } from 'src/util/i18n/currency';

import {
  BUY, CHART_HEIGHT, ChartAxisStyle, SELL, TICK_COUNT,
  chartPadding,
} from './chartConstants';
import {
  buildBarWidth, buildCurrencyAxisFormatter, chartDomain, handleTickFormat,
} from '../helpers/chart';
import {
  barColor, chartStateOpacity, formFinalChartData,
} from '../helpers/common';

const ChartWrapper = styled.div`
  svg {
    overflow: visible;
    position: relative;
    z-index: 999;
  }
`;

/**
 * Build and returns victory bar component for the trade and meter chart
 * @param {object} domain
 * @param {object} chartData
 * @param {string} chartOpacity
 * @param {string} key - residual, community ...
 * @param {Function} handleChartClick
 * @param {number} barWidth
 * @returns {React.ReactElement} - bar charts.
 */
const chartBars = (
  domain,
  chartData,
  chartOpacity,
  key,
  handleChartClick,
  barWidth,
) => (
  <VictoryBar
    barWidth={barWidth}
    data={chartData}
    domain={domain}
    // React 19 removed defaultProps for function components, so Victory no
    // longer injects its default labelComponent. Without an explicit one,
    // getLabelPlacement reads labelComponent.props on undefined and throws.
    labelComponent={<VictoryLabel />}
    style={{
      data: {
        fill: ({ datum }) => (barColor(datum.y, key)), opacity: chartOpacity,
      },
    }}
    events={[
      {
        target: 'data',
        eventHandlers: {
          onClick: () => {
            handleChartClick();
          },
        },
      },
    ]}
  />
);

/**
 * Build and returns trade and bar chart
 * @param {object} domain
 * @param {object} data - trade or meter data
 * @param {Function} compareByXFunc
 * @param {Function} handleChartClick
 * @param {number} barWidth
 * @param {Array} hoverKeys
 * @param {Array} selectedKeys
 * @returns {React.ReactElement} - bar chart stacks.
 */
const chartStacks = (
  domain,
  data,
  compareByXFunc,
  handleChartClick,
  barWidth,
  hoverKeys = [],
  selectedKeys = [],
) => (
  <VictoryStack>
    {[SELL, BUY].map((direction) => {
      const rawChartData = direction === SELL
        ? Object.keys(data[direction]).reverse() : Object.keys(data[direction]);

      const series = rawChartData.map((key) => {
        const directionData = data[direction][key];
        if (directionData && Object.keys(directionData).length > 0) {
          const finalChartData = formFinalChartData(
            Object.values(directionData).sort(compareByXFunc),
            direction,
          );
          const chartOpacity = chartStateOpacity(key, hoverKeys, selectedKeys);
          return (
            chartBars(
              domain,
              finalChartData,
              chartOpacity,
              key,
              handleChartClick,
              barWidth,
            )
          );
        }
        return null;
      });
      return direction === BUY ? series.reverse() : series;
    })}
  </VictoryStack>
);

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - StackedBarChart component
 */
export default function StackedBarChart({
  chartView, stackBarProps = null, stepSize = 'P1D', timespan,
  timezone, toolTipProps = null, width, yAxisFormat,
}) {
  const {
    data,
    hoverKeys,
    selectedKeys,
    compareByXFunc,
    tooltipUpdateFunc,
    handleChartClick,
    chartType,
  } = stackBarProps;

  const {
    unitLabel,
  } = toolTipProps;

  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  let formatter = yAxisFormat;
  const chartLocale = getChartLocale(intl.formatMessage, intl.locale);
  const currencyChartLocale = yAxisFormat.startsWith('$')
    ? getCurrencyChartLocale(intlCurrency)
    : null;
  if (chartLocale && typeof (chartLocale) === 'object') {
    timeFormatDefaultLocale(chartLocale);
    // Tooltips, summaries and cards format energy and carbon with d3's default
    // number locale, so keep it on the UI language. Currency values use explicit
    // partner-locale formatters instead.
    formatDefaultLocale(chartLocale);
    const numberLocale = currencyChartLocale || chartLocale;
    const numberFormatter = formatLocale(numberLocale);

    // Use smart currency formatter to avoid milli prefix for values < $1
    // (e.g., display "$0.50" instead of "$500m")
    if (yAxisFormat.startsWith('$')) {
      formatter = buildCurrencyAxisFormatter(numberFormatter, yAxisFormat);
    } else {
      formatter = numberFormatter.format(yAxisFormat);
    }
  }
  const { xAxisDomain } = chartView || {};
  // Memoized so its object identity is stable across tooltip-only re-renders,
  // which lets the `stacks` memo below actually skip recomputation.
  const domain = useMemo(
    () => chartDomain(data, stepSize, timespan, xAxisDomain),
    [data, stepSize, timespan, xAxisDomain],
  );
  const scale = scaleZoned(timezone).domain(domain).range([0, width]);
  const barWidth = buildBarWidth(width, stepSize, domain);

  // Memoize the per-series sort + formFinalChartData transform (and the bar
  // elements it produces) so hovering a bar (which only changes tooltipTimestamp
  // upstream, not any of these inputs) does not re-sort and re-map every series
  // on every render.
  const stacks = useMemo(() => {
    if (!width || width <= 0) {
      return null;
    }

    return chartStacks(
      domain,
      data,
      compareByXFunc,
      handleChartClick,
      barWidth,
      hoverKeys,
      selectedKeys,
    );
  }, [width, domain, data, compareByXFunc, handleChartClick, barWidth,
    hoverKeys, selectedKeys]);

  // Guard: do not render until the container width has been measured.
  // Rendering a VictoryChart with width=0 produces a degenerate SVG viewBox
  // that causes the browser to scale axis / tooltip text unpredictably.
  // Placed after all hooks so hook call order stays stable across renders.
  if (!width || width <= 0) {
    return null;
  }

  return (
    <ChartWrapper>
      <VictoryChart
        scale={{ x: scale, y: 'linear' }}
        domain={domain}
        height={CHART_HEIGHT}
        width={width}
        padding={chartPadding}
        containerComponent={(
          <VictoryVoronoiContainer
            labels={() => ' '}
            labelComponent={(
              <VictoryTooltip
                flyoutComponent={(
                  <ChartTooltip
                    axisFormat={yAxisFormat}
                    chartType={chartType}
                    chartView={chartView}
                    chartWidth={width}
                    tooltipProps={toolTipProps}
                    tooltipUpdateFunc={tooltipUpdateFunc}
                  />
                )}
              />
            )}
            mouseFollowTooltips
            voronoiDimension="x"
          />
        )}
      >
        {stacks}

        <VictoryAxis
          tickCount={TICK_COUNT}
          offsetY={20}
          style={ChartAxisStyle}
          fixLabelOverlap
          tickFormat={(tick) => handleTickFormat(timezone)(tick)}
        />
        <VictoryAxis
          label={yAxisFormat.startsWith('$')
            ? getCurrencySymbol(intlCurrency)
            : unitLabel}
          axisLabelComponent={<VictoryLabel dx={0} dy={-15} />}
          style={ChartAxisStyle}
          crossAxis={false}
          orientation="left"
          dependentAxis
          fixLabelOverlap
          tickFormat={(t) => formatter(t)}
        />
      </VictoryChart>

    </ChartWrapper>
  );
}

StackedBarChart.propTypes = {
  chartView: PropTypes.shape({
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_METER,
      DATA_AGGREGATE_BY_PORTFOLIO,
      DATA_AGGREGATE_BY_PROPERTY,
    ]).isRequired,
    xAxisDomain: PropTypes.oneOf([
      X_AXIS_DOMAIN_FIXED,
      X_AXIS_DOMAIN_DYNAMIC,
    ]).isRequired,
  }).isRequired,
  stackBarProps: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.object]),
    hoverKeys: PropTypes.oneOfType([PropTypes.array]),
    selectedKeys: PropTypes.oneOfType([PropTypes.array]),
    compareByXFunc: PropTypes.func.isRequired,
    tooltipUpdateFunc: PropTypes.func.isRequired,
    handleChartClick: PropTypes.func,
    chartType: PropTypes.string,
  }),
  stepSize: PropTypes.oneOf(['P1D', 'PT30M']),
  timespan: PropTypes.shape({
    start: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    finish: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  timezone: PropTypes.string.isRequired,
  toolTipProps: PropTypes.shape({
    unit: PropTypes.string,
    unitLabel: PropTypes.string,
    multipleMeter: PropTypes.bool,
    tooltipDateFormat: PropTypes.oneOfType([PropTypes.object]),
    tooltipData: PropTypes.oneOfType([PropTypes.object]),
    tooltipLabelFunc: PropTypes.func,
    tooltipFormat: PropTypes.string,
    isCarbon: PropTypes.bool.isRequired,
  }),
  width: PropTypes.number.isRequired,
  yAxisFormat: PropTypes.string.isRequired,
};
